// Placeholder content for pages the backend doesn't support yet (ratings, reviews, amenities,
// events, a standalone offers catalog). Deliberately isolated in one file so it's obvious what's
// real (from api.ts) vs. illustrative, and easy to rip out once a real backend exists for these.

// Deterministic per-id "rating" so the same real venue always shows the same stars/review count
// across pages, without a Rating column on Resto/Shop.
export function mockRatingFor(id: number): { rating: number; reviewCount: number } {
  const rating = Math.round((4.3 + ((id * 37) % 60) / 100) * 10) / 10; // 4.3–4.9
  const reviewCount = 80 + ((id * 173) % 1200);
  return { rating, reviewCount };
}

export const categories = ['Clubs', 'Pubs & Bars', 'Restaurants', 'Resorts', 'Wine Shops', 'Events'] as const;

// Resto records have no Category column yet, so deterministically bucket each real venue into
// one of the four non-wine-shop categories by id — keeps Explore's category filter meaningful
// without fabricating a whole taxonomy backend.
const RESTO_CATEGORIES = ['Clubs', 'Pubs & Bars', 'Restaurants', 'Resorts'] as const;
export function mockCategoryFor(id: number): (typeof RESTO_CATEGORIES)[number] {
  return RESTO_CATEGORIES[id % RESTO_CATEGORIES.length];
}

export const amenitiesPool = ['Live DJ', 'Signature Cocktails', 'VIP Tables', 'Dance Floor', 'Food Menu', 'Valet Parking', 'Outdoor Seating'];

export function mockAmenitiesFor(id: number): string[] {
  const count = 3 + (id % 3);
  return amenitiesPool.slice(id % 2, id % 2 + count);
}

export interface MockReview {
  name: string;
  rating: number;
  comment: string;
  date: string;
}

const reviewPool: MockReview[] = [
  { name: 'Aarav M.', rating: 5, comment: 'Best night out in the city — the DJ and crowd were unreal.', date: '2 weeks ago' },
  { name: 'Priya S.', rating: 4, comment: 'Great vibe, drinks took a while but worth the wait.', date: '1 month ago' },
  { name: 'Rohan K.', rating: 5, comment: 'Claimed the offer at the door, super smooth process.', date: '3 weeks ago' },
  { name: 'Sneha P.', rating: 4, comment: 'Lovely ambience, will definitely come back with friends.', date: '5 days ago' }
];

export function mockReviewsFor(id: number): MockReview[] {
  const count = 2 + (id % 3);
  return reviewPool.slice(0, count);
}

export interface MockOffer {
  id: number;
  title: string;
  venue: string;
  category: 'Nightlife' | 'Restaurants' | 'Resorts' | 'Wine Shops';
  location: string;
  validity: string;
  image: string;
}

export const mockOffers: MockOffer[] = [
  { id: 1, title: 'Free Entry + 1 Drink', venue: 'Club Amazonia', category: 'Nightlife', location: 'Downtown, Pune', validity: 'Tonight · Until 11:30 PM', image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=800&q=60' },
  { id: 2, title: '20% Off Couple Entry', venue: 'Sunset Villa', category: 'Nightlife', location: 'Beverly Hills, Pune', validity: 'Valid This Week', image: 'https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=800&q=60' },
  { id: 3, title: 'All Inclusive Pass', venue: 'Lakeview Resort', category: 'Resorts', location: 'Lake Geneva, Pune', validity: 'Valid This Month', image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=800&q=60' },
  { id: 4, title: 'Buy 2 Get 1 Free', venue: 'The Urban Tap', category: 'Restaurants', location: 'Koregaon Park, Pune', validity: 'Every Weekend', image: 'https://images.unsplash.com/photo-1544148103-0773bf10d330?auto=format&fit=crop&w=800&q=60' },
  { id: 5, title: '15% Off on Food', venue: 'Spice Garden', category: 'Restaurants', location: 'Viman Nagar, Pune', validity: 'Valid Till Month End', image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=60' },
  { id: 6, title: '10% Off Wine', venue: 'Relax Wines', category: 'Wine Shops', location: 'Pimprichinchwad, Pune', validity: 'Ongoing', image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=800&q=60' }
];

export interface MockEvent {
  id: number;
  day: string;
  month: string;
  title: string;
  venue: string;
  location: string;
  time: string;
  category: 'Clubs' | 'Pubs' | 'Resorts' | 'Special Events';
  badge: string;
  image: string;
}

export const mockEvents: MockEvent[] = [
  { id: 1, day: '25', month: 'DEC', title: 'Christmas Night Party', venue: 'Club Amazonia', location: 'Downtown, Pune', time: '8:00 PM', category: 'Clubs', badge: 'Live DJ', image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=800&q=60' },
  { id: 2, day: '31', month: 'DEC', title: 'New Year Bash 2025', venue: 'Sunset Villa', location: 'Beverly Hills, Pune', time: '9:00 PM', category: 'Special Events', badge: 'House Party', image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=800&q=60' },
  { id: 3, day: '12', month: 'JAN', title: 'Bollywood Night', venue: 'The Urban Tap', location: 'Koregaon Park, Pune', time: '9:00 PM', category: 'Pubs', badge: 'Bollywood', image: 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=800&q=60' },
  { id: 4, day: '26', month: 'JAN', title: 'Republic Day Special', venue: 'Lakeview Resort', location: 'Lake Geneva, Pune', time: '7:00 PM', category: 'Resorts', badge: 'Special Event', image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=800&q=60' }
];

// Category badge accent colors, per the reference design's "special offer/category accents may
// use purple/pink/cyan/green" guidance — used sparingly, just for these small badges.
const CATEGORY_ACCENTS: Record<string, string> = {
  Clubs: '#F5C542',
  'Pubs & Bars': '#EC4899',
  Restaurants: '#F5A742',
  Restaurant: '#F5A742',
  Resorts: '#22D3EE',
  Resort: '#22D3EE',
  'Wine Shops': '#EC4899',
  Events: '#A855F7',
  Nightlife: '#EC4899'
};
export function categoryAccent(category?: string): string {
  return (category && CATEGORY_ACCENTS[category]) || '#F5C542';
}

export const stats = [
  { label: 'Venues', value: '500+' },
  { label: 'Happy Users', value: '50K+' },
  { label: 'Offers', value: '1000+' },
  { label: 'Cities', value: '10+' }
];
